import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Input } from '../components/ui/Input';
import { TextArea } from '../components/ui/TextArea';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { callBillingAPI } from '../utils/api';
import { CheckCircleIcon, AlertCircleIcon, UploadIcon, XIcon } from 'lucide-react';
export function PublicWholesaleSetup() {
  const {
    companyId
  } = useParams<{
    companyId: string;
  }>();
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    schoolName: '',
    schoolAddress: '',
    email: '',
    phone: ''
  });
  // Signature canvas
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  // File upload
  const [file, setFile] = useState<File | null>(null);
  const [fileBase64, setFileBase64] = useState<string>('');
  // UI state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = 150;
    // Set drawing style
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);
  // Canvas drawing handlers
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    setIsDrawing(true);
    setHasSignature(true);
    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.nativeEvent.offsetY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.nativeEvent.offsetY;
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };
  // File upload handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setFileBase64(reader.result as string);
    };
    reader.readAsDataURL(selectedFile);
  };
  const removeFile = () => {
    setFile(null);
    setFileBase64('');
  };
  // Form validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.schoolName.trim()) {
      newErrors.schoolName = 'School name is required';
    }
    if (!formData.schoolAddress.trim()) {
      newErrors.schoolAddress = 'School address is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    if (!hasSignature) {
      newErrors.signature = 'Signature is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    setSubmitError(null);
    try {
      // Get signature as base64
      const canvas = canvasRef.current;
      if (!canvas) throw new Error('Signature canvas not found');
      const signatureData = canvas.toDataURL('image/png');
      // Split name into first and last
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || '';
      // Submit to API
      await callBillingAPI('publicSubmitWholesaleRequest', {
        company_id: companyId,
        first_name: firstName,
        last_name: lastName,
        school_name: formData.schoolName,
        school_address: formData.schoolAddress,
        email: formData.email,
        phone: formData.phone,
        signature_data: signatureData,
        ownership_proof: fileBase64 || undefined
      });
      setIsSuccess(true);
    } catch (error) {
      console.error('Failed to submit wholesale request:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  // Success state
  if (isSuccess) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-2453">
        <Card className="max-w-md w-full" data-id="element-2454">
          <div className="text-center py-8" data-id="element-2455">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4" data-id="element-2456">
              <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-2457" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2458">
              Request Submitted!
            </h1>
            <p className="text-gray-600" data-id="element-2459">
              Thank you! Your wholesale account request has been submitted. We
              will review your application and contact you soon.
            </p>
          </div>
        </Card>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 py-8 px-4" data-id="element-2460">
      <div className="max-w-2xl mx-auto" data-id="element-2461">
        {/* Header */}
        <div className="text-center mb-8" data-id="element-2462">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2463">
            Wholesale Account Request
          </h1>
          <p className="text-gray-600" data-id="element-2464">
            Apply for a wholesale account for your martial arts school
          </p>
        </div>

        {/* Form Card */}
        <Card data-id="element-2465">
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2466">
            {/* Name */}
            <Input label="Your Name *" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} error={errors.name} placeholder="John Doe" data-id="element-2467" />

            {/* School Name */}
            <Input label="School Name *" value={formData.schoolName} onChange={e => setFormData({
            ...formData,
            schoolName: e.target.value
          })} error={errors.schoolName} placeholder="Dragon Martial Arts Academy" data-id="element-2468" />

            {/* School Address */}
            <TextArea label="School Address *" value={formData.schoolAddress} onChange={e => setFormData({
            ...formData,
            schoolAddress: e.target.value
          })} error={errors.schoolAddress} placeholder="123 Main Street, City, State, ZIP" rows={3} data-id="element-2469" />

            {/* Email */}
            <Input label="Email *" type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} error={errors.email} placeholder="john@example.com" data-id="element-2470" />

            {/* Phone */}
            <Input label="Mobile Phone *" type="tel" value={formData.phone} onChange={e => setFormData({
            ...formData,
            phone: e.target.value
          })} error={errors.phone} placeholder="(555) 123-4567" data-id="element-2471" />

            {/* File Upload */}
            <div data-id="element-2472">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2473">
                Ownership Proof (Optional)
              </label>
              <p className="text-sm text-gray-500 mb-2" data-id="element-2474">
                Tax Document or Business License with Martial Arts School Name
              </p>

              {!file ? <div className="relative" data-id="element-2475">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} className="hidden" id="file-upload" data-id="element-2476" />
                  <label htmlFor="file-upload" className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors" data-id="element-2477">
                    <UploadIcon className="w-5 h-5 text-gray-400" data-id="element-2478" />
                    <span className="text-sm text-gray-600" data-id="element-2479">
                      Click to upload file
                    </span>
                  </label>
                </div> : <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-2480">
                  <span className="text-sm text-gray-700 truncate flex-1" data-id="element-2481">
                    {file.name}
                  </span>
                  <button type="button" onClick={removeFile} className="ml-2 p-1 text-gray-400 hover:text-red-600 transition-colors" data-id="element-2482">
                    <XIcon className="w-4 h-4" data-id="element-2483" />
                  </button>
                </div>}
            </div>

            {/* Signature */}
            <div data-id="element-2484">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2485">
                Signature *
              </label>
              <p className="text-sm text-gray-600 mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-2486">
                I legally swear I own a Martial Arts School and qualify for a
                Wholesale Account
              </p>

              <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white" data-id="element-2487">
                <canvas ref={canvasRef} onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} className="w-full cursor-crosshair touch-none" style={{
                height: '150px'
              }} data-id="element-2488" />
              </div>

              <div className="flex justify-between items-center mt-2" data-id="element-2489">
                <Button type="button" variant="ghost" size="sm" onClick={clearSignature} disabled={!hasSignature} data-id="element-2490">
                  Clear Signature
                </Button>
                {errors.signature && <p className="text-sm text-red-600" data-id="element-2491">{errors.signature}</p>}
              </div>
            </div>

            {/* Submit Error */}
            {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-2492">
                <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-2493" />
                <div className="flex-1" data-id="element-2494">
                  <p className="text-sm font-medium text-red-900" data-id="element-2495">
                    Submission Failed
                  </p>
                  <p className="text-sm text-red-700 mt-1" data-id="element-2496">{submitError}</p>
                </div>
              </div>}

            {/* Submit Button */}
            <div className="pt-4 border-t border-gray-200" data-id="element-2497">
              <Button type="submit" className="w-full" isLoading={isLoading} disabled={isLoading} data-id="element-2498">
                Submit Wholesale Request
              </Button>
            </div>
          </form>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500" data-id="element-2499">
          <p data-id="element-2500">Questions? Contact us for assistance.</p>
        </div>
      </div>
    </div>;
}